<template>
  <b-form @submit.prevent="submitForm">
    <div v-if="isLoading" class="py-3 text-center text-secondary">
      {{ $t("loadingData") }}
    </div>
    <template v-else-if="selectedSubscriptionPlan">
      <div class="border-bottom pb-3 mb-3">
        <b-form-row>
          <b-col cols="6"><label>{{ $t("subscriptionInfo") }}</label></b-col>
          <b-col cols="6" style="overflow-wrap: break-word;width: 200px;"><b>{{ selectedSubscriptionPlan.name }}</b></b-col>
        </b-form-row>
        <!-- subscriptionLength: Thời hạn sử dụng -->
        <b-form-row>
          <b-col cols="6">{{ $t("subscriptionLength") }}</b-col>
          <b-col cols="6" style="display: flex;">
            <b-form-input style="width: 50px;font-weight: bold;margin-right: 4px;"
              v-model="selectedSubscriptionPlan.numberOfDaysUsage" type="number" :min="durationLimits().min"
              :max="durationLimits().max" required size="sm" />
            <b style="margin-right: 4px;"> {{ selectedSubscriptionPlan.unitTimeVN }}</b>
          </b-col>
        </b-form-row>
        <b-form-row v-for="(row, idx) in planInfoRows" :key="idx">
          <b-col cols="6">{{ $t(row.label) }}</b-col>
          <b-col cols="6">
            <b>{{ row.value }}</b>
            <b v-if="row.suffix" style="margin-left: 4px;">{{ $t(row.suffix) }}</b>
          </b-col>
        </b-form-row>
      </div>
      <div class="mb-3">
        <label>{{ $t("serviceInfo") }}</label>
        <b-form-row>
          <b-col cols="6">{{ $t("currentExpDate") }}</b-col>
          <b-col cols="6"><b>{{ currentExpDateStr }}</b></b-col>
        </b-form-row>
        <b-form-row>
          <b-col cols="6">{{ $t("expDateAfterRenew") }}</b-col>
          <b-col cols="6"><b>{{ expDateStr }}</b></b-col>
        </b-form-row>
        <b-form-row>
          <b-col cols="6">{{ $t("servicePrice") }}</b-col>
          <b-col cols="6"><b>{{ priceStr }} VND</b></b-col>
        </b-form-row>
        <b-form-row>
          <b-col cols="6">{{ $t("vatTax") }}</b-col>
          <b-col cols="6"><b>{{ taxAmountStr }} VND</b></b-col>
        </b-form-row>
        <b-form-row>
          <b-col cols="6">{{ $t("amountCustomerToPay") }}</b-col>
          <b-col cols="6"><b>{{ dueAmountStr }} VND</b></b-col>
        </b-form-row>
      </div>
      <p class="text-secondary">{{ $t("renewConfirmCorrectInfo") }}</p>
      <div v-if="formErrorMsg" class="text-danger">{{ formErrorMsg }}</div>
      <div class="text-right mt-4">
        <b-button type="button" variant="secondary" class="mr-3" @click="onClickCancel" :disabled="isSubmitting">{{
          $t("cancel") }}</b-button>
        <b-button type="submit" variant="primary" :disabled="isSubmitting">{{ $t("renewService") }}</b-button>
      </div>
    </template>
  </b-form>
</template>

<style lang="scss" scoped></style>

<script lang="ts">
import { Component, Emit, Prop } from 'vue-property-decorator';
import _ from "lodash";
import moment, { Moment } from "moment";
import BaseComponent from "@/components/BaseComponent";
import * as subscriptionPlanService from "@/service/subscriptionPlanService";
import * as customerService from "@/service/customerService";
import BigNumber from "bignumber.js";
import { SubscriptionUnitTime } from "@/views/enums/unitTime";
import * as subscriptionUtil from "@/service/subscriptionUtil";
import { ManagementType } from "@/views/enums/subscriptionAction";

@Component
export default class ExtendSubscriptionModal extends BaseComponent {
  datePickerFormatOptions = { year: 'numeric', month: '2-digit', day: '2-digit' };
  @Prop({ required: true }) customerSubscription;
  @Prop({ default: 0 }) meetingConcurrentParticipantsLimit_s;
  @Prop({ default: 0 }) numberOfConcurrentMeetings_s;
  @Emit("submitted") __submitted() { }

  isLoading = true;
  subscriptionPlans = [];
  subscriptionPlanId: number = null;
  startTime: string = null;
  formErrorMsg = "";
  isSubmitting = false;
  numberOfConcurrentMeetings = 0;
  meetingConcurrentParticipantsLimit = 0;


  // Emit decorators only

  mounted() {
    this.meetingConcurrentParticipantsLimit = +(this.meetingConcurrentParticipantsLimit_s);
    this.numberOfConcurrentMeetings = +(this.numberOfConcurrentMeetings_s);
    this.startTime = moment().format("YYYY-MM-DD");
    this.fetchSubscriptionPlans();
  }
  durationLimits() {
    switch (this.selectedSubscriptionPlan.unitTime) {
      case SubscriptionUnitTime.months:
        return { min: 1, max: 36 };
      case SubscriptionUnitTime.years:
        return { min: 1, max: 3 };
      case SubscriptionUnitTime.days:
        return { min: 1, max: 365 };
      default:
        return { min: 1, max: 36 };
    }
  }
  async fetchSubscriptionPlans() {
    try {
      this.isLoading = true;
      this.subscriptionPlans = await subscriptionPlanService.getPlans();
    } catch (e) {
      this.toastError(e);

    } finally {
      this.isLoading = false;
    }
  }


  get subscriptionPlanOptions() {
    const options: any[] = this.subscriptionPlans.map(it => ({
      value: it.id,
      text: it.name + " - " + this.formatCurrencyAmount(it.price) + " VND",
    }));
    options.unshift({ text: this.$t("chooseSubscription").toString(), value: null, disabled: true });
    return options;
  }


  get selectedSubscriptionPlan() {
    return this.subscriptionPlans.find(it => it.id === this.customerSubscription?.subscriptionPlanId);
  }

  get planInfoRows() {
    const active = {
      maxParticipantPool: this.customerSubscription.maxParticipantPool,
      meetingConcurrentParticipantsLimit: this.meetingConcurrentParticipantsLimit,
      numberOfConcurrentMeetings: this.numberOfConcurrentMeetings
    };
    return subscriptionUtil.buildPlanInfoRows(active, this.selectedSubscriptionPlan);
  }


  get startTimeMoment(): Moment {
    return moment.utc(this.startTime, "YYYY-MM-DD", true);
  }


  // Ngày hết hạn hiện tại
  get currentExpDateStr(): string {
    return moment(this.customerSubscription.endTime).format("DD/MM/YYYY");
  }


  //numberOfDaysUsage: hiện thị ngày khi được cộng số tháng nhập vào
  get expDateStr(): string {
    let newStartTimeMoment = moment(this.customerSubscription.endTime);
    if (newStartTimeMoment.isBefore()) {
      newStartTimeMoment = moment();
    }
    return newStartTimeMoment
      .add(this.selectedSubscriptionPlan.numberOfDaysUsage, this.selectedSubscriptionPlan.unitTime)
      .format("DD/MM/YYYY");
  }


  // Hiển thị giá tiền chưa thuế
  get priceStr(): string {
    return this.formatCurrencyAmount(this.selectedSubscriptionPlan.price);
  }


  // Hiển thị thuế VAT
  get taxAmountStr(): string {
    return this.formatCurrencyAmount(
      new BigNumber(this.selectedSubscriptionPlan.price)
        .multipliedBy(0.1)
        .decimalPlaces(0)
        .toNumber()
    );
  }


  // Hiển thị giá tiền đã bao gồm thuế
  get dueAmountStr(): string {
    return this.formatCurrencyAmount(
      new BigNumber(this.selectedSubscriptionPlan.price)
        .multipliedBy(1.1)
        .decimalPlaces(0)
        .toNumber()
    );
  }


  onClickCancel() {
    this.$bvModal.hide("extend-subscription-modal");
  }


  async submitForm() {
    try {
      this.isSubmitting = true;
      const isPool = this.selectedSubscriptionPlan?.managementType === ManagementType.CONCURRENT_PARTICIPANT_POOL;
      const numMeetings = isPool ? null : +this.numberOfConcurrentMeetings;
      await customerService.renewSubscription(
        this.customerSubscription.id,
        numMeetings,
        this.selectedSubscriptionPlan.numberOfDaysUsage
      );
      this.__submitted();
      this.$bvModal.hide("extend-subscription-modal");
      this.toastSuccessDelay(this.$t("renewServiceSuccess").toString());

    } catch (e) {
      this.toastError(e);

    } finally {
      this.isSubmitting = false;
    }
  }
}
</script>
